import { useMemo, useState } from "react";
import {
    ArrowLeft,
    Building2,
    ChevronRight,
    History,
    KeyRound,
    Pencil,
    Plus,
    ShieldX,
    Trash2,
    Users as UsersIcon,
} from "lucide-react";
import { Link, Navigate, useParams } from "@tanstack/react-router";
import { toast } from "sonner";

import { ApiError } from "~/lib/api";
import { useCurrentUser } from "~/lib/auth";
import { lookupCountry } from "~/lib/countries";
import { type LicenseRecord, useLicenses } from "~/lib/licenses";
import {
    type TenantContactKind,
    type TenantContactRecord,
    useDeleteContact,
    useTenant,
} from "~/lib/tenants";
import { useUsers } from "~/lib/users";

import { ContactDialog } from "~/components/ContactDialog";
import { EditOrganizationDialog } from "~/components/EditOrganizationDialog";
import { LicenseDetailDialog } from "~/components/LicenseDetailDialog";
import { TableSkeleton } from "~/components/TableSkeleton";
import { TenantRowActions } from "~/components/TenantRowActions";

const TENANT_STATUS_PILL: Record<string, string> = {
    active: "status-pill--live",
    suspended: "status-pill--paused",
    archived: "status-pill--queued",
};

const LICENSE_STATUS_PILL: Record<LicenseRecord["status"], string> = {
    active: "status-pill--live",
    suspended: "status-pill--paused",
    expired: "status-pill--queued",
    revoked: "status-pill--failed",
};

const CONTACT_KIND_LABEL: Record<TenantContactKind, string> = {
    primary: "Primary",
    billing: "Billing",
    technical: "Technical",
    legal: "Legal",
    security: "Security",
};

function formatDate(iso: string): string {
    return new Date(iso).toLocaleDateString(undefined, {
        year: "numeric",
        month: "short",
        day: "numeric",
    });
}

export function TenantDetailPage(): React.ReactElement {
    const me = useCurrentUser();
    const isAdmin = me.data?.roles.includes("admin");
    const { tenantId } = useParams({ from: "/app/tenants/$tenantId" });
    const tenantQ = useTenant(tenantId);
    const licensesQ = useLicenses();
    const usersQ = useUsers();

    const [editOpen, setEditOpen] = useState(false);
    const [contactOpen, setContactOpen] = useState(false);
    const [contactEdit, setContactEdit] = useState<TenantContactRecord | null>(null);
    const [activeLicense, setActiveLicense] = useState<LicenseRecord | null>(null);

    const deleteContact = useDeleteContact(tenantId);

    const tenantLicenses = useMemo(
        () => licensesQ.data?.filter((l) => l.tenantId === tenantId) ?? [],
        [licensesQ.data, tenantId],
    );
    const tenantUsers = useMemo(
        () => usersQ.data?.filter((u) => u.tenantId === tenantId) ?? [],
        [usersQ.data, tenantId],
    );

    if (me.data && !isAdmin) {
        return <Navigate to="/" />;
    }

    if (tenantQ.isLoading) {
        return (
            <div className="stack-lg">
                <header className="page-header">
                    <div className="page-header__top">
                        <div className="page-header__heading">
                            <span className="page-header__eyebrow">
                                <span className="page-header__eyebrow__icon"><Building2 size={14} aria-hidden /></span>
                                Identity &amp; access
                            </span>
                            <h1 className="page-header__title">Loading…</h1>
                        </div>
                    </div>
                </header>
                <section className="section-card">
                    <TableSkeleton rows={4} columns={3} />
                </section>
            </div>
        );
    }

    if (tenantQ.isError || !tenantQ.data) {
        const notFound =
            tenantQ.error instanceof ApiError && tenantQ.error.status === 404;
        return (
            <div className="stack-lg">
                <section className="section-card">
                    <div className="empty-state">
                        <ShieldX size={20} className="empty-state__icon" aria-hidden />
                        <div className="empty-state__title">
                            {notFound ? "Tenant not found" : "Could not load tenant"}
                        </div>
                        <div className="empty-state__body">
                            {notFound
                                ? "That tenant id does not exist or has been archived."
                                : tenantQ.error instanceof Error
                                    ? tenantQ.error.message
                                    : "Unknown error."}
                        </div>
                        <Link to="/tenants" className="btn btn--ghost" style={{ marginTop: "var(--space-3)" }}>
                            <ArrowLeft size={14} />
                            <span>Back to tenants</span>
                        </Link>
                    </div>
                </section>
            </div>
        );
    }

    const tenant = tenantQ.data;
    const countryInfo = lookupCountry(tenant.country);

    const addressLines = [
        tenant.addressLine1,
        tenant.addressLine2,
        [tenant.city, tenant.state, tenant.postalCode].filter(Boolean).join(" "),
        countryInfo ? `${countryInfo.name} (${countryInfo.code})` : tenant.country,
    ].filter((v): v is string => typeof v === "string" && v.trim().length > 0);

    async function onDeleteContact(c: TenantContactRecord): Promise<void> {
        if (!window.confirm(`Delete contact “${c.name}”?`)) return;
        try {
            await deleteContact.mutateAsync(c.id);
            toast.success("Contact deleted");
        } catch (err) {
            toast.error(err instanceof ApiError ? err.message : "Failed to delete contact");
        }
    }

    return (
        <div className="stack-lg">
            <header className="page-header">
                <div className="page-header__top">
                    <div className="page-header__heading">
                        <span className="page-header__eyebrow">
                            <span className="page-header__eyebrow__icon"><Building2 size={14} aria-hidden /></span>
                            <Link to="/tenants" className="page-header__eyebrow__crumb">Tenants</Link>
                            <ChevronRight size={12} aria-hidden />
                            <span>Organization</span>
                        </span>
                        <h1 className="page-header__title">{tenant.name}</h1>
                        <p className="page-header__description">
                            <code className="type-mono-sm">{tenant.slug}</code>
                            {tenant.legalName && (
                                <>
                                    {" · "}
                                    <span className="text-text-muted">{tenant.legalName}</span>
                                </>
                            )}
                        </p>
                    </div>
                    <div className="page-header__actions cluster-xs">
                        <button
                            type="button"
                            className="btn btn--primary press-crush"
                            onClick={() => setEditOpen(true)}
                        >
                            <Pencil size={14} />
                            <span>Edit organization</span>
                        </button>
                        <TenantRowActions tenant={tenant} />
                    </div>
                </div>
                <div className="page-header__meta">
                    <span className={`status-pill ${TENANT_STATUS_PILL[tenant.status]}`}>
                        {tenant.status}
                    </span>
                    {countryInfo && (
                        <span className="page-header__meta__item">
                            <span aria-hidden>{countryInfo.flag}</span>
                            <span>{countryInfo.code}</span>
                        </span>
                    )}
                    {!countryInfo && tenant.country && (
                        <span className="page-header__meta__item">{tenant.country}</span>
                    )}
                    <span className="page-header__meta__item tabular-nums">
                        {tenant.userCount} users
                    </span>
                    <span className="page-header__meta__item tabular-nums">
                        {tenant.activeLicenseCount} active licenses
                    </span>
                    <span className="page-header__meta__item">
                        Created {formatDate(tenant.createdAt)}
                    </span>
                </div>
            </header>

            {/* Organization section */}
            <section className="section-card" aria-label="Organization">
                <header className="section-card__header">
                    <div>
                        <div className="section-card__title">Organization</div>
                        <div className="section-card__description">
                            Legal and billing envelope for this customer.
                        </div>
                    </div>
                </header>
                <div
                    style={{
                        display: "grid",
                        gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
                        gap: "var(--space-4)",
                        padding: "var(--space-4)",
                    }}
                >
                    <Field label="Legal name" value={tenant.legalName} />
                    <Field label="Industry"   value={tenant.industry} />
                    <Field
                        label="Country"
                        value={countryInfo ? `${countryInfo.flag} ${countryInfo.name} (${countryInfo.code})` : tenant.country}
                    />
                    <Field label="Tax ID"     value={tenant.taxId} mono />
                    <Field label="Website"    value={tenant.website} link />
                    <Field
                        label="Address"
                        value={addressLines.length ? addressLines.join("\n") : undefined}
                        multiline
                    />
                    <Field label="Notes"      value={tenant.notes} multiline />
                </div>
            </section>

            {/* Contacts section */}
            <section className="section-card" aria-label="Contacts">
                <header className="section-card__header">
                    <div>
                        <div className="section-card__title">Contacts</div>
                        <div className="section-card__description">
                            Named human POCs at this organization. Distinct from platform users.
                        </div>
                    </div>
                    <div className="toolbar">
                        <button
                            type="button"
                            className="btn btn--primary btn--sm press-crush"
                            onClick={() => {
                                setContactEdit(null);
                                setContactOpen(true);
                            }}
                        >
                            <Plus size={14} />
                            <span>Add contact</span>
                        </button>
                    </div>
                </header>
                {tenant.contacts.length === 0 ? (
                    <div className="empty-state">
                        <UsersIcon size={20} className="empty-state__icon" aria-hidden />
                        <div className="empty-state__title">No contacts yet</div>
                        <div className="empty-state__body">
                            Add a primary, billing, or technical POC so the right person gets reached
                            on incidents and renewals.
                        </div>
                    </div>
                ) : (
                    <div style={{ overflowX: "auto" }}>
                        <table className="data-table">
                            <thead>
                                <tr>
                                    <th style={{ whiteSpace: "nowrap", width: 1 }}>Kind</th>
                                    <th>Name</th>
                                    <th>Email</th>
                                    <th>Title</th>
                                    <th>Phone</th>
                                    <th style={{ width: 1 }} aria-label="Actions" />
                                </tr>
                            </thead>
                            <tbody>
                                {tenant.contacts.map((c) => (
                                    <tr key={c.id}>
                                        <td style={{ whiteSpace: "nowrap" }}>
                                            <span className="chip chip--subtle">
                                                {CONTACT_KIND_LABEL[c.kind]}
                                            </span>
                                        </td>
                                        <td className="text-13">{c.name}</td>
                                        <td>
                                            <a href={`mailto:${c.email}`} className="text-13 link">
                                                {c.email}
                                            </a>
                                        </td>
                                        <td className="text-13 text-text-muted">{c.title ?? ""}</td>
                                        <td className="text-13 text-text-muted">{c.phone ?? ""}</td>
                                        <td style={{ textAlign: "right", whiteSpace: "nowrap" }}>
                                            <button
                                                type="button"
                                                className="btn btn--ghost btn--sm"
                                                aria-label="Edit contact"
                                                onClick={() => {
                                                    setContactEdit(c);
                                                    setContactOpen(true);
                                                }}
                                            >
                                                <Pencil size={14} />
                                            </button>
                                            <button
                                                type="button"
                                                className="btn btn--ghost btn--sm"
                                                aria-label="Delete contact"
                                                onClick={() => void onDeleteContact(c)}
                                            >
                                                <Trash2 size={14} />
                                            </button>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </section>

            {/* Licenses section */}
            <section className="section-card" aria-label="Licenses">
                <header className="section-card__header">
                    <div>
                        <div className="section-card__title">Licenses</div>
                        <div className="section-card__description">
                            Product grants issued to this tenant.
                        </div>
                    </div>
                </header>
                {licensesQ.isLoading ? (
                    <TableSkeleton rows={3} columns={4} />
                ) : tenantLicenses.length === 0 ? (
                    <div className="empty-state">
                        <KeyRound size={20} className="empty-state__icon" aria-hidden />
                        <div className="empty-state__title">No licenses yet</div>
                        <div className="empty-state__body">
                            Issue the first license from the <Link to="/licenses" className="link">Licenses</Link> page.
                        </div>
                    </div>
                ) : (
                    <div style={{ overflowX: "auto" }}>
                        <table className="data-table">
                            <thead>
                                <tr>
                                    <th>Name</th>
                                    <th>Product</th>
                                    <th style={{ whiteSpace: "nowrap", width: 1 }}>Status</th>
                                    <th style={{ whiteSpace: "nowrap", width: 1 }}>Valid from</th>
                                    <th style={{ whiteSpace: "nowrap", width: 1 }}>Expires</th>
                                    <th style={{ textAlign: "right", whiteSpace: "nowrap", width: 1 }}>Keys</th>
                                </tr>
                            </thead>
                            <tbody>
                                {tenantLicenses.map((l) => (
                                    <tr
                                        key={l.id}
                                        onClick={() => setActiveLicense(l)}
                                        style={{ cursor: "pointer" }}
                                    >
                                        <td className="text-13">{l.name}</td>
                                        <td className="text-13 text-text-muted">
                                            <code className="type-mono-sm">{l.productSlug}</code>
                                        </td>
                                        <td style={{ whiteSpace: "nowrap" }}>
                                            <span className={`status-pill ${LICENSE_STATUS_PILL[l.status]}`}>
                                                {l.status}
                                            </span>
                                        </td>
                                        <td className="text-12 text-text-muted" style={{ whiteSpace: "nowrap" }}>
                                            {l.validFrom ? formatDate(l.validFrom) : "—"}
                                        </td>
                                        <td className="text-12 text-text-muted" style={{ whiteSpace: "nowrap" }}>
                                            {l.expiresAt ? formatDate(l.expiresAt) : "—"}
                                        </td>
                                        <td className="text-13 text-text-muted tabular" style={{ textAlign: "right" }}>
                                            {l.apiKeyCount}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </section>

            {/* Users section */}
            <section className="section-card" aria-label="Users">
                <header className="section-card__header">
                    <div>
                        <div className="section-card__title">Users</div>
                        <div className="section-card__description">
                            Platform users scoped to this tenant.
                        </div>
                    </div>
                </header>
                {usersQ.isLoading ? (
                    <TableSkeleton rows={3} columns={3} />
                ) : tenantUsers.length === 0 ? (
                    <div className="empty-state">
                        <UsersIcon size={20} className="empty-state__icon" aria-hidden />
                        <div className="empty-state__title">No users yet</div>
                        <div className="empty-state__body">
                            Invite users from the <Link to="/users" className="link">Users</Link> page and scope them to this tenant.
                        </div>
                    </div>
                ) : (
                    <div style={{ overflowX: "auto" }}>
                        <table className="data-table">
                            <thead>
                                <tr>
                                    <th>Email</th>
                                    <th>Roles</th>
                                    <th style={{ whiteSpace: "nowrap", width: 1 }}>Status</th>
                                    <th style={{ whiteSpace: "nowrap", width: 1 }}>Created</th>
                                </tr>
                            </thead>
                            <tbody>
                                {tenantUsers.map((u) => (
                                    <tr key={u.id}>
                                        <td className="text-13">{u.email}</td>
                                        <td>
                                            <span className="cluster-xs">
                                                {u.roles.map((r) => (
                                                    <span key={r} className="chip chip--subtle">{r}</span>
                                                ))}
                                            </span>
                                        </td>
                                        <td style={{ whiteSpace: "nowrap" }}>
                                            <span className="status-pill">{u.status}</span>
                                        </td>
                                        <td className="text-12 text-text-muted" style={{ whiteSpace: "nowrap" }}>
                                            {formatDate(u.createdAt)}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </section>

            {/* Audit placeholder */}
            <section className="section-card" aria-label="Audit history">
                <header className="section-card__header">
                    <div>
                        <div className="section-card__title">Audit history</div>
                        <div className="section-card__description">
                            Every mutation on this tenant will surface here once the audit
                            service lands.
                        </div>
                    </div>
                </header>
                <div className="empty-state">
                    <History size={20} className="empty-state__icon" aria-hidden />
                    <div className="empty-state__title">Coming in 1G</div>
                    <div className="empty-state__body">
                        Audit history arrives with the platform audit service in Phase 1G.
                    </div>
                </div>
            </section>

            <EditOrganizationDialog
                tenant={tenant}
                open={editOpen}
                onOpenChange={setEditOpen}
            />
            <ContactDialog
                tenantId={tenantId}
                contact={contactEdit}
                open={contactOpen}
                onOpenChange={setContactOpen}
            />
            <LicenseDetailDialog
                license={activeLicense}
                onClose={() => setActiveLicense(null)}
            />
        </div>
    );
}

interface FieldProps {
    label: string;
    value?: string | null;
    mono?: boolean;
    link?: boolean;
    multiline?: boolean;
}

function Field({ label, value, mono, link, multiline }: FieldProps): React.ReactElement {
    const v = typeof value === "string" && value.trim() !== "" ? value : null;
    return (
        <div className="stack-xs">
            <span className="text-12 text-text-muted">{label}</span>
            {v === null ? (
                <span className="text-13 text-text-subtle">—</span>
            ) : link ? (
                <a
                    href={v.startsWith("http") ? v : `https://${v}`}
                    target="_blank"
                    rel="noreferrer noopener"
                    className="text-13 link"
                    style={{ overflowWrap: "anywhere" }}
                >
                    {v}
                </a>
            ) : multiline ? (
                <span
                    className={mono ? "type-mono-sm" : "text-13"}
                    style={{ whiteSpace: "pre-line", overflowWrap: "anywhere" }}
                >
                    {v}
                </span>
            ) : (
                <span
                    className={mono ? "type-mono-sm" : "text-13"}
                    style={{ overflowWrap: "anywhere" }}
                >
                    {v}
                </span>
            )}
        </div>
    );
}
